import { SUITE_FEATURE_GROUPINGS } from "@silversea-ssc/web-suite-features"
import { ComparableSuite, FeatureRow, FeatureTemplate, SuiteFeatureValue } from "@silversea-ssc/ui-components-react"
import { match, Pattern } from "ts-pattern"
import { t } from "@lingui/macro"
import { i18n } from "@lingui/core"
import * as F from "fp-ts/function"
import * as A from "fp-ts/Array"
import { PriceResponseV2, UnitSystem } from "@ssc-booking/api-contracts"
import { FareContent, SanityImage, SanitySuiteComparison } from "../../app-core/_common/sanity/sanity-types"
import { stringIsPresent } from "../../app-core/_common/checks"
import { getSectionKeys } from "../SuiteModal/SuiteModalContent"
import { toFormattedPriceValue } from "../../app-core/_common/format/price"
import { SuiteWithPriceAndConfiguration } from "../../app-core/features/_common/read-models/suite-category"

type SanityFeatureValue = string | boolean | SanityImage | undefined

export const template = (totalNumberOfDecks: number | undefined): FeatureTemplate => ({
    groups: [
        {
            variant: "big",
            sectionTitle: "Basics",
            features: [
                { label: i18n._(t`Suite Size`), type: "string", key: "basics_suite_size" },
                { label: i18n._(t`Deck (of ${totalNumberOfDecks})`), type: "string", key: "basics_decks" },
                { label: i18n._(t`Ship Section`), type: "string", key: "basics_ship_sections" },
            ],
        },
        {
            variant: "big",
            sectionTitle: "Layout",
            features: [{ label: i18n._(t`Suite Plan`), type: "string", key: "suite_plan" }],
        },
        ...SUITE_FEATURE_GROUPINGS.map((section) => ({
            variant: "small" as const,
            sectionTitle: section.title,
            features: section.features.map(
                (feature): FeatureRow => ({
                    label: i18n._(feature.key),
                    type: feature.type == "boolean" ? "boolean" : "string",
                    key: feature.key,
                }),
            ),
        })),
    ],
})

type SuiteWithPrice = SuiteWithPriceAndConfiguration<SanitySuiteComparison>

export const toComparableSuite =
    (unit: UnitSystem, faresContent: FareContent[]) =>
    (
        suiteWithPrice: SuiteWithPriceAndConfiguration<SanitySuiteComparison>,
    ): ComparableSuite & { availability?: PriceResponseV2["availability"] } => {
        const { content, price } = suiteWithPrice

        const formattedPrice = toFormattedPriceValue(faresContent, price)
        const enhancedFeatures = buildEnhancedFeatures(content, unit)

        return {
            ...content,
            suiteCategory: price.suiteCategory,
            availability: price.availability,
            price: formattedPrice,
            features: buildComparableSuiteFeatures(content, enhancedFeatures),
        }
    }

type SuiteContent = SuiteWithPrice["content"]
type EnhancedFeatures = Record<string, SanityFeatureValue>

const buildEnhancedFeatures = (suite: SuiteContent, unit: UnitSystem): EnhancedFeatures => {
    const useFeet = unit === "feet"
    const shortSizeDescription = useFeet
        ? suite.configuration.shortSizeDescriptionInSquareFeet
        : suite.configuration.shortSizeDescriptionInSquareMetres

    const verandaSizeDescription = useFeet
        ? suite.configuration.verandaSizeInSquareFeet
        : suite.configuration.verandaSizeInSquareMetres

    return {
        ...suite.features,
        basics_suite_size: `${shortSizeDescription}
                            ${verandaSizeDescription ? `(${verandaSizeDescription})` : ""}`,
        basics_decks: suite.deck.join(", "),
        basics_ship_sections: getSectionKeys(suite.shipSections)
            .map((key) => i18n._(key))
            .join(", "),

        suite_plan: suite.configuration.plan,
    }
}

const buildComparableSuiteFeatures = (
    suite: SuiteContent,
    enhancedFeatures: EnhancedFeatures,
): ComparableSuite["features"] =>
    F.pipe(
        Object.entries(enhancedFeatures),
        A.map(([key, value]) => {
            const caveatValue = suite.features[`${key}_caveat`] as string | undefined
            return [
                key,
                toSuiteFeatureValue(translateValue(value), stringIsPresent(caveatValue) ? caveatValue : undefined),
            ]
        }),
        Object.fromEntries,
    )

const translateValue = (key: SanityFeatureValue) =>
    match(key)
        .with(Pattern.boolean, (v) => v)
        .with(Pattern.string, (v) => i18n._(v))
        .otherwise(() => key)

const toSuiteFeatureValue = (
    value: SanityFeatureValue,
    caveatValue: string | undefined,
): SuiteFeatureValue | undefined =>
    match([value, caveatValue] as const)
        .with([Pattern.string, Pattern.nullish], ([value]) => ({
            type: "string" as const,
            value,
        }))
        .with([Pattern.string, Pattern.string], ([value, caveat]) => ({
            type: "string-caveat" as const,
            value,
            caveat,
        }))
        .with([Pattern.boolean, Pattern.nullish], ([value]) => ({
            type: "boolean" as const,
            value,
        }))
        .with([Pattern.boolean, Pattern.string], ([value, caveat]) => ({
            type: "boolean-caveat" as const,
            value,
            caveat,
        }))
        .with([{ asset: { _id: Pattern.string } }, Pattern.any], ([value]) => ({
            type: "image" as const,
            value,
        }))
        .with([Pattern.nullish, Pattern.any], () => ({
            type: "boolean" as const,
            value: false,
        }))
        .exhaustive()
